import {makePropsAreEqual} from '@wandb/weave/common/util/shouldUpdate';
import classNames from 'classnames';
import _ from 'lodash';
import React, {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react';

import {useSweepsContext} from '../../../pages/Sweeps/SweepsContext';
import {Analytics, trackMinMaxAggregation} from '../../../services/analytics';
import {RunWithRunsetInfo} from '../../../state/runs/types';
import {DispatchableAction} from '../../../types/redux';
import {TABLE_MIN_COLUMN_WIDTH} from '../../../util/constants';
import {ValueOp} from '../../../util/filterTypes';
import {Sort as QuerySort} from '../../../util/queryTypes';
import {Config as RunFeedDataConfig} from '../../../util/runfeed';
import {keyString, keyToCss, keyToString} from '../../../util/runs';
import {Key as RunKey, Value as RunValue} from '../../../util/runTypes';
import * as WBTreeHelpers from '../../../util/wbtree';
import {TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {useRunSelectorResizingContext} from '../../MultiRunWorkspace/RunSelectorResizingContext';
import type {RunSelectorRunsPartialVars} from '../types';
import {ResetRunColors} from './ResetRunColors';
import {WBTableColumn, WBTableHoverCellCoords, WBTableRowFields} from './types';
import {
  CustomMenuGroup,
  isMenuGroup,
  MenuGroup,
} from './WBTableCellColumnHeaderMenu';
import {WBTableColumnHeaders} from './WBTableColumnHeaders';
import {WBTableRow} from './WBTableRow';
import {WBTableSortIndicatorComponent} from './WBTableSortIndicator';

export const HIDE_COLUMN_SORT_CONTROLS = ['Tags', 'GPU Type', 'GPU Count'];

interface WBTableGridProps {
  topLevelQueryVariables: RunSelectorRunsPartialVars;
  loadingTable: boolean; // from parent
  treeRef?: React.RefObject<HTMLDivElement>;
  className?: string;
  pinned?: boolean;
  columns: WBTableColumn[];
  fixedColumns: string[];
  rows: Array<WBTableRowFields<RunWithRunsetInfo>>;
  expandedRowAddresses: string[];
  columnDragAccessor?: string;
  columnDropAccessor?: string;
  columnResizingAccessor?: string;
  childrenPaginationMap: {
    [key: string]: number | undefined;
  };
  hoverCellCoords: WBTableHoverCellCoords;
  tableSettings: RunFeedDataConfig;
  leftMargin: number;
  scrollXBounds: [number, number];
  expandable?: boolean;
  expanded?: boolean;
  isSingleMode?: boolean;
  isInReport?: boolean;
  showArtifactCounts?: boolean;
  showLogCounts?: boolean;
  readOnly?: boolean;
  SortIndicatorComponent?: WBTableSortIndicatorComponent;
  truncationType: TruncationType;
  sort: QuerySort;
  setTableSettings(config: Partial<RunFeedDataConfig>): void;
  setTableState(stateUpdate: {
    columnDragAccessor?: string;
    columnDropAccessor?: string;
    columnResizingAccessor?: string;
    columnResizeOffset?: number;
  }): void;
  setHoverCellCoords(newCoords: WBTableHoverCellCoords): void;
  setChildrenPagination(rowAddress: string, activePage?: number): void;
  addGroup(newGroup: RunKey): void;
  moveColumn(): void;
  togglePinnedColumn(columnAccessor: string): void; // add column to (or remove from) pinnedColumnKeys
  hideColumn(columnAccessor: string): void;
  replaceColumnAggregation(
    oldColumnAccessor: string,
    newColumnAccessor: string
  ): void;
  columnKeySupportsAggregates(key: RunKey): boolean;
  toggleExpandedRow(rowAddress: string): void; // expand or collapse this row (add/remove rowAddress from expandedRowAddresses)
  openSortPopup?(): void;
  // Query actions
  updateSort(updateFn: (sort: QuerySort) => void): void;
  updateTruncationType(truncationType: TruncationType): DispatchableAction;
  addFilter(key: RunKey, op: ValueOp, value: RunValue): void;
}

export interface VisibleColumns {
  leftPadding: number;
  rightPadding: number;
  visibleColumnRange: [number, number];
}

export const getVisibleColumns = (
  scrollXBounds: [number, number],
  columns: WBTableColumn[],
  columnWidths?: {
    [keys: string]: number;
  }
): VisibleColumns => {
  const actualWidths: number[] = Array(columns.length);
  let totalWidth = 0;
  columns.forEach((column, columnIndex) => {
    const columnKeyString = keyToString(column.key);
    const columnWidth =
      (columnWidths && columnWidths[columnKeyString]) ||
      WBTreeHelpers.defaultColumnWidth(column.key);
    actualWidths[columnIndex] = columnWidth;
    totalWidth += columnWidth;
  });

  let [minX, maxX] = scrollXBounds;
  const span = maxX - minX;

  const windowingBuffer = 500;
  let currentX = 0;
  let minColumn = columns.length;
  let maxColumn = 0;
  let leftPadding = 0;
  let rightPadding = 0;

  if (minX > totalWidth - span - windowingBuffer) {
    minX = totalWidth - span - windowingBuffer;
    maxX = totalWidth + windowingBuffer;
  }

  columns.forEach((column, columnIndex) => {
    const columnWidth = actualWidths[columnIndex];
    const colLeftEdge = currentX;
    const colRightEdge = currentX + columnWidth;
    if (colRightEdge >= minX - windowingBuffer) {
      // column is visible because right edge is within buffer
      minColumn = Math.min(minColumn, columnIndex);
    } else {
      leftPadding += columnWidth;
    }
    if (colLeftEdge <= maxX + windowingBuffer) {
      // column is visible because left edge is within buffer
      maxColumn = Math.max(maxColumn, columnIndex + 1);
    } else {
      rightPadding += columnWidth;
    }
    currentX += columnWidth;
  });

  return {
    leftPadding,
    rightPadding,
    visibleColumnRange: [minColumn, maxColumn],
  };
};

const getSortKeyId = (sort: QuerySort, key: RunKey) => {
  const sortKey = sort.keys.find(sk => _.isEqual(sk.key, key));
  const isSortKeyAscending = sortKey?.ascending;
  if (isSortKeyAscending === undefined) {
    return undefined;
  }
  return isSortKeyAscending ? 'sortasc' : 'sortdesc';
};

const WBTableGridComp: React.FC<WBTableGridProps> = ({
  addFilter,
  addGroup,
  childrenPaginationMap,
  className = '',
  columnDragAccessor,
  columnDropAccessor,
  columnKeySupportsAggregates,
  columns,
  expandable,
  expanded,
  expandedRowAddresses,
  fixedColumns,
  hideColumn,
  hoverCellCoords,
  isInReport,
  isSingleMode,
  leftMargin,
  loadingTable,
  moveColumn,
  openSortPopup,
  pinned,
  readOnly,
  replaceColumnAggregation,
  rows,
  scrollXBounds,
  setChildrenPagination,
  setHoverCellCoords,
  setTableSettings,
  setTableState,
  showArtifactCounts,
  showLogCounts,
  sort,
  SortIndicatorComponent,
  tableSettings,
  toggleExpandedRow,
  togglePinnedColumn,
  topLevelQueryVariables,
  treeRef,
  truncationType,
  updateSort,
  updateTruncationType,
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const {isSweeps} = useSweepsContext();

  const {
    isResizing: isRunSelectorResizing,
    columnResizingAccessor,
    setColumnResizingAccessor,
  } = useRunSelectorResizingContext();

  const trickPanelsIntoResizing = useCallback(() => {
    // panels adapt to window resizes,
    // so just mock a resize
    window.dispatchEvent(new Event('resize'));
  }, []);

  useLayoutEffect(() => {
    trickPanelsIntoResizing();
  }, [trickPanelsIntoResizing]);

  const columnMenuGroups = (
    column: WBTableColumn
  ): (MenuGroup | CustomMenuGroup)[] => {
    const groups: (MenuGroup | CustomMenuGroup)[] = [];
    if (fixedColumns.indexOf(column.accessor) === -1) {
      groups.push({
        items: [
          {
            id: 'pin',
            name: `${pinned ? 'Unpin' : 'Pin'} column`,
            icon: 'pin',
            onSelect: () => {
              togglePinnedColumn(column.accessor);
              const actionLocation = isInReport ? 'report' : 'runs table';
              Analytics.track('Pin column clicked', {
                location: actionLocation,
              });
            },
          },
          {
            id: 'hide',
            name: `Hide column`,
            icon: 'hide-hidden',
            onSelect: () => {
              hideColumn(column.accessor);
              const actionLocation = isInReport ? 'report' : 'runs table';
              Analytics.track('Hide column clicked', {
                location: actionLocation,
              });
            },
          },
        ],
      });
    }
    const key = column.key;
    if (key == null) {
      return groups;
    }
    if (key.section === 'config') {
      if (!groups.length) {
        groups.push({items: []});
      }
      const group = groups[groups.length - 1];
      if (isMenuGroup(group)) {
        group.items.push({
          id: 'groupby',
          name: 'Group by',
          icon: 'group',
          onSelect: () => {
            addGroup(key);
          },
        });
      }
    }

    const handleSortValueChange = (newValue: string) => {
      const isAscending = newValue.startsWith('sortasc');
      if (_.isUndefined(columnDragAccessor) && column.displayName !== 'Tags') {
        updateSort(prevSort => {
          if (prevSort.keys.length <= 1) {
            prevSort.keys = [{key, ascending: isAscending}];
            return;
          }
          const sortKey = prevSort.keys.find(sk => _.isEqual(sk.key, key));
          if (sortKey != null) {
            sortKey.ascending = isAscending;
          } else {
            prevSort.keys.push({key, ascending: isAscending});
          }
          openSortPopup?.();
        });
      }
      const actionLocation = isInReport ? 'report' : 'runs table';
      Analytics.track('Sort column clicked', {
        direction: isAscending ? 'asc' : 'desc',
        location: actionLocation,
      });
    };

    const sortKeyId = getSortKeyId(sort, key);
    const sortGroup: MenuGroup = {
      items: [],
      header: 'Sorting order',
      value: sortKeyId + ':' + key.name + key.section,
      onValueChange: handleSortValueChange,
    };

    if (!_.includes(HIDE_COLUMN_SORT_CONTROLS, column.displayName)) {
      groups.push(sortGroup);
      const columnSortTextAsc =
        column.displayName === 'Created' ? 'Oldest first' : 'Ascending';
      sortGroup.items.push({
        id: 'sortasc' + ':' + key.name + key.section,
        name: columnSortTextAsc,
        icon: 'sort-ascending',
        isItemIndicatorCheckmark: true,
      });
      const columnSortTextDesc =
        column.displayName === 'Created' ? 'Newest first' : 'Descending';
      sortGroup.items.push({
        id: 'sortdesc' + ':' + key.name + key.section,
        name: columnSortTextDesc,
        icon: 'sort-descending',
        isItemIndicatorCheckmark: true,
      });
    }

    const handleTruncationTypeChange = (newValue: string) => {
      updateTruncationType(newValue as TruncationType);

      const actionLocation = isInReport ? 'report' : 'runs table';
      Analytics.track('Truncate text clicked', {
        type: newValue,
        location: actionLocation,
      });
    };

    /**
     * This is a conditional menu group that exposes buttons which reassign and reset custom colors on the runs currently active in the run selector. See: https://wandb.atlassian.net/browse/WB-24486
     *
     * We do not want to enable this on sweeps pages currently because we don't have a good way to get the SharedPanelRefs context, so this implementation is a bit of hack to avoid rendering a useResetColors hook in a sweeps page where it won't reliably exist.
     */
    const resetRunColors: CustomMenuGroup = {
      id: 'reset-run-colors',
      items: [],
      header: 'Reset run colors',
      render: () => {
        return <ResetRunColors sort={sort} rows={rows} />;
      },
      type: 'custom',
    };
    if (!isSweeps) {
      groups.push(resetRunColors);
    }

    const runNameTruncationGroup: MenuGroup = {
      items: [],
      header: 'Run name cropping',
      value: truncationType,
      onValueChange: handleTruncationTypeChange,
    };

    if (column.accessor === 'run:displayName') {
      groups.push(runNameTruncationGroup);
      runNameTruncationGroup.items.push({
        id: TruncationType.End,
        name: 'Crop the end',
        icon: 'crop-end',
        isItemIndicatorCheckmark: true,
      });

      runNameTruncationGroup.items.push({
        id: TruncationType.Middle,
        name: 'Crop the middle',
        icon: 'crop-middle',
        isItemIndicatorCheckmark: true,
      });

      runNameTruncationGroup.items.push({
        id: TruncationType.Beginning,
        name: 'Crop the beginning',
        icon: 'crop-beginning',
        isItemIndicatorCheckmark: true,
      });
    }

    if (columnKeySupportsAggregates(key)) {
      let value = 'latest';
      if (key.section === 'aggregations_min') {
        value = 'min';
      } else if (key.section === 'aggregations_max') {
        value = 'max';
      }
      const onValueChange = (id: string) => {
        if (id === 'min') {
          replaceColumnAggregation(
            column.accessor,
            keyString('aggregations_min', key.name)
          );
          trackMinMaxAggregation({type: id});
        } else if (id === 'max') {
          replaceColumnAggregation(
            column.accessor,
            keyString('aggregations_max', key.name)
          );
          trackMinMaxAggregation({type: id});
        } else {
          replaceColumnAggregation(
            column.accessor,
            keyString('summary', key.name)
          );
          trackMinMaxAggregation({type: 'latest'});
        }
      };
      groups.push({
        items: [
          {
            id: 'aggregations-submenu',
            name: `Show ${value}`,
            icon: 'x-axi-alt',
            groups: [
              {
                value,
                onValueChange,
                items: [
                  {id: 'latest', name: 'latest'},
                  {id: 'min', name: 'min'},
                  {id: 'max', name: 'max'},
                ],
              },
            ],
          },
        ],
      });
    }
    return groups;
  };

  const visibleColumns = getVisibleColumns(
    scrollXBounds,
    columns,
    tableSettings.columnWidths
  );
  const [minColumn, maxColumn] = visibleColumns.visibleColumnRange;
  const columnCount = maxColumn - minColumn;

  const columnsSlice = useMemo(
    () => columns.slice(minColumn, maxColumn),
    [columns, maxColumn, minColumn]
  );

  let columnHeight =
    treeRef && treeRef.current && !isNaN(treeRef.current.clientHeight)
      ? treeRef.current.clientHeight - 44
      : 0;
  if (expandable) {
    // adjust for 52px of padding used to
    // prevent pagination controls from blocking text
    columnHeight -= 52;
  }

  return (
    <div
      ref={wrapperRef}
      className={`wb-tree--${pinned ? 'pinned-' : ''}wrapper`}>
      {!pinned && (
        // This crazy thing was the only way I could get the
        // header background to be full width and sticky
        // when the grid isn't full width.
        // Let me know if you find a better way.
        <div
          className="wb-tree-header-background-wrapper"
          style={{
            pointerEvents: 'none',
            position: 'absolute',
            left: 0,
            right: 0,
            height: columnHeight,
            marginBottom: -columnHeight,
          }}>
          <div className="wb-tree-header-background" />
        </div>
      )}
      <div
        ref={treeRef}
        className={classNames('wb-tree', className, {
          'wb-tree--pinned': pinned,
          'wb-tree--loading': loadingTable,
          'border-dashed': isRunSelectorResizing,
        })}
        style={{
          gridTemplateColumns: `${visibleColumns.leftPadding}px repeat(${columnCount}, fit-content(${TABLE_MIN_COLUMN_WIDTH}px)) ${visibleColumns.rightPadding}px`,
          marginLeft: leftMargin,
        }}>
        {pinned && <div className="wb-tree-header-background" />}

        <WBTableColumnHeaders
          displayedRows={rows}
          columns={columnsSlice}
          columnWidths={tableSettings.columnWidths}
          columnDragAccessor={columnDragAccessor}
          columnDropAccessor={columnDropAccessor}
          columnResizingAccessor={columnResizingAccessor}
          hoverCellCoords={hoverCellCoords}
          expanded={expanded}
          SortIndicatorComponent={SortIndicatorComponent}
          isInReport={isInReport}
          childProps={(column, columnIndex) => {
            const columnAccessor = column.accessor;
            const actualColumnIndex = columnIndex + minColumn;
            return {
              readOnly,
              column,
              columnIndex: actualColumnIndex,
              columnWidth:
                (tableSettings.columnWidths &&
                  tableSettings.columnWidths[columnAccessor]) ||
                WBTreeHelpers.defaultColumnWidth(column.key),
              draggable: fixedColumns.indexOf(column.accessor) === -1,
              hovering:
                columnResizingAccessor == null &&
                hoverCellCoords &&
                hoverCellCoords[1] === keyToCss(column.key),
              cellHoverProps: {
                onMouseEnter: () => {
                  setHoverCellCoords([undefined, keyToCss(column.key)]);
                },
                onMouseLeave: () => {
                  setHoverCellCoords([]);
                },
              },
              columnMenuGroups: columnMenuGroups(column),
              columnMovingProps: {
                dragging: columnDragAccessor === columnAccessor,
                dropping: columnDropAccessor === columnAccessor,
                dragHandleProps: {
                  onMouseDown: () =>
                    setTableState({columnDragAccessor: columnAccessor}),
                  onMouseUp: () =>
                    setTableState({columnDragAccessor: undefined}),
                },
                onDragEnter: () => {
                  setTableState({columnDropAccessor: columnAccessor});
                },
                onDragEnd: () => {
                  setTableState({
                    columnDragAccessor: undefined,
                    columnDropAccessor: undefined,
                  });
                },
                onDrop: () => {
                  if (columnDragAccessor) {
                    moveColumn();
                  }
                },
              },
              columnResizingProps: {
                resizing: columnResizingAccessor === columnAccessor,
                onResizeStart: () => {
                  setColumnResizingAccessor(columnAccessor);
                },
                onResize: offset => {
                  setTableState({columnResizeOffset: offset});
                },
                resizeColumn: (newWidth: number) => {
                  setColumnResizingAccessor(undefined);
                  setTableState({columnResizeOffset: 0});
                  setTableSettings({
                    columnWidths: {
                      ...tableSettings.columnWidths,
                      [columnAccessor]: newWidth,
                    },
                  });
                  trickPanelsIntoResizing();
                },
              },
            };
          }}
        />
        <div className="wb-tree-padding" />
        {columnsSlice.map((column, columnIndex) => {
          const columnAccessor = column.accessor;
          const keyCss = keyToCss(column.key);
          const hovering =
            columnResizingAccessor == null &&
            hoverCellCoords &&
            hoverCellCoords[1] === keyCss &&
            keyCss !== 'run_name';
          const resizing = columnResizingAccessor === columnAccessor;
          const dropping = columnDropAccessor === columnAccessor;
          return (
            <div
              style={{
                height: Math.max(columnHeight, 0),
                marginBottom: Math.min(-columnHeight, 0),
              }}
              key={`background-${columnIndex}`}
              className={`wb-tree-cell-column-background${
                hovering ? ' wb-tree-cell--hovering' : ''
              }${resizing ? ' wb-tree-cell--resizing' : ''}${
                dropping ? ' wb-tree-cell--dropping' : ''
              }`}
            />
          );
        })}
        <div className="wb-tree-padding" />

        {rows.map(r => {
          return (
            <WBTableRow
              addFilter={addFilter}
              childrenPaginationMap={childrenPaginationMap}
              columns={columnsSlice}
              expanded={expanded}
              expandedRowAddresses={expandedRowAddresses}
              hoverCellCoords={hoverCellCoords}
              isSingleMode={isSingleMode}
              key={r.__address__}
              pinned={pinned}
              recursionDepth={0}
              row={r}
              setChildrenPagination={setChildrenPagination}
              setHoverCellCoords={setHoverCellCoords}
              showArtifactCounts={showArtifactCounts}
              showLogCounts={showLogCounts}
              toggleExpandedRow={toggleExpandedRow}
              topLevelQueryVariables={topLevelQueryVariables}
              truncationType={truncationType}
            />
          );
        })}
      </div>
    </div>
  );
};

export const WBTableGrid = memo(WBTableGridComp, (prevProps, nextProps) => {
  if (
    !_.isEqual(
      getVisibleColumns(
        prevProps.scrollXBounds,
        prevProps.columns,
        prevProps.tableSettings.columnWidths
      ),
      getVisibleColumns(
        nextProps.scrollXBounds,
        nextProps.columns,
        nextProps.tableSettings.columnWidths
      )
    )
  ) {
    return false;
  }
  if (nextProps.rows.length !== prevProps.rows.length) {
    return false;
  }
  for (let i = 0; i < prevProps.rows.length; i++) {
    if (prevProps.rows[i].__address__ !== nextProps.rows[i].__address__) {
      return false;
    }
  }
  const propsAreEqual = makePropsAreEqual({
    name: 'WBTableGrid',
    deep: [
      'tempSelectedCount',
      'fixedColumns',
      'expandedRowAddresses',
      'columns',
      'tableSettings',
      // 'topLevelQueryVariables',
    ],
    // handled in shouldComponentUpdate
    ignore: ['scrollXBounds'],
    ignoreFunctions: true,
    debug: false,
    verbose: true,
  });
  return propsAreEqual(prevProps, nextProps);
});
